import React from 'react';
import { Helmet } from 'react-helmet';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import Icon from 'components/AppIcon';
import { Link } from 'react-router-dom';

const TrainingDevelopmentHub = () => {
  const trainingModules = [
    {
      icon: 'Laptop',
      title: 'Transformation Numérique',
      description: 'Maîtrisez les outils digitaux et les technologies émergentes pour propulser votre organisation.',
      topics: [
        'Digitalisation des processus métiers',
        'Outils collaboratifs et productivité',
        'Gestion de projets numériques',
        'Cloud computing et cybersécurité'
      ]
    },
    {
      icon: 'Users',
      title: 'Leadership & Management',
      description: 'Développez des compétences de leadership moderne pour inspirer et guider vos équipes.',
      topics: [
        'Leadership transformationnel',
        'Management d\'équipes hybrides',
        'Communication et influence',
        'Gestion du changement'
      ]
    },
    {
      icon: 'Brain',
      title: 'Intelligence Artificielle',
      description: 'Intégrez l\'IA de manière stratégique et éthique dans vos processus organisationnels.',
      topics: [
        'Fondamentaux de l\'IA pour décideurs',
        'IA générative et productivité',
        'Automatisation intelligente',
        'Éthique et gouvernance de l\'IA'
      ]
    },
    {
      icon: 'Target',
      title: 'Soft Skills & Performance',
      description: 'Renforcez les compétences comportementales essentielles à la réussite professionnelle.',
      topics: [
        'Intelligence émotionnelle',
        'Résolution de problèmes complexes',
        'Créativité et innovation',
        'Gestion du stress et résilience'
      ]
    }
  ];

  const benefits = [
    {
      icon: 'Award',
      title: 'Certifications Reconnues',
      description: 'Obtenez des certifications professionnelles valorisées sur le marché international.'
    },
    {
      icon: 'Users',
      title: 'Formateurs Experts',
      description: 'Apprenez auprès de consultants actifs avec une expertise terrain reconnue.'
    },
    {
      icon: 'Settings',
      title: 'Parcours Sur Mesure',
      description: 'Formations personnalisées selon vos besoins spécifiques et objectifs stratégiques.'
    },
    {
      icon: 'TrendingUp',
      title: 'ROI Mesurable',
      description: 'Impact quantifiable sur la performance individuelle et organisationnelle.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Formation & Développement | CRF PERFECTION - Excellence Professionnelle</title>
        <meta 
          name="description" 
          content="Excellence en Formation : Des programmes innovants qui transforment les compétences professionnelles et accélèrent la croissance des équipes. Investissement stratégique en capital humain." 
        />
        <meta name="keywords" content="formation professionnelle, développement compétences, programmes innovation, transformation équipes, excellence professionnelle, compétences numériques" />
        <meta property="og:title" content="Formation & Développement | CRF PERFECTION" />
        <meta 
          property="og:description" 
          content="Programmes innovants qui transforment les compétences professionnelles et accélèrent la croissance des équipes." 
        />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        {/* Section 1: Hero */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(155, 142, 87, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(155, 142, 87, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }} />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                  <Icon name="GraduationCap" size={20} style={{ color: '#9B8E57' }} />
                  <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                    Excellence en Formation Professionnelle
                  </span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  Formation & Développement
                </h1>
                
                <p className="text-xl md:text-2xl font-semibold" style={{ color: '#9B8E57' }}>
                  Transformez les compétences, accélérez la croissance
                </p>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Dans un monde en mutation constante, l'apprentissage continu est la clé de la performance durable. 
                    Nos programmes de formation sont conçus pour développer les compétences critiques qui feront la 
                    différence en 2026 et au-delà.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    <strong>Le capital humain est votre premier levier de création de valeur.</strong> Investissez 
                    dans des formations qui transforment durablement vos équipes et votre organisation.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                    style={{ 
                      backgroundColor: '#96121D',
                      boxShadow: '0 4px 14px rgba(150, 18, 29, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#7d0f18';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#96121D';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Demander un Devis
                    <Icon name="ArrowRight" size={20} className="ml-2" />
                  </Link>
                  
                  <Link 
                    to="/service-request-form"
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg border-2 transition-all duration-300"
                    style={{ 
                      borderColor: '#9B8E57',
                      color: '#9B8E57'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#9B8E57';
                      e.target.style.color = '#ffffff';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#9B8E57';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Explorer les Programmes
                    <Icon name="BookOpen" size={20} className="ml-2" />
                  </Link>
                </div>
              </div>

              {/* Right Content - Stats Cards */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                         style={{ backgroundColor: 'rgba(155, 142, 87, 0.2)' }}>
                      <Icon name="Users" size={24} style={{ color: '#9B8E57' }} />
                    </div>
                  </div>
                  <div className="text-3xl font-bold mb-1">1000+</div>
                  <div className="text-gray-300 text-sm">Professionnels Formés</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                         style={{ backgroundColor: 'rgba(150, 18, 29, 0.2)' }}>
                      <Icon name="Award" size={24} style={{ color: '#96121D' }} />
                    </div>
                  </div>
                  <div className="text-3xl font-bold mb-1">98%</div>
                  <div className="text-gray-300 text-sm">Taux de Satisfaction</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                         style={{ backgroundColor: 'rgba(155, 142, 87, 0.2)' }}>
                      <Icon name="Globe" size={24} style={{ color: '#9B8E57' }} />
                    </div>
                  </div>
                  <div className="text-3xl font-bold mb-1">International</div>
                  <div className="text-gray-300 text-sm">USA & Afrique</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                         style={{ backgroundColor: 'rgba(150, 18, 29, 0.2)' }}>
                      <Icon name="BookOpen" size={24} style={{ color: '#96121D' }} />
                    </div>
                  </div>
                  <div className="text-3xl font-bold mb-1">4 Modules</div>
                  <div className="text-gray-300 text-sm">Domaines Clés</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Modules de Formation */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos Modules de Formation
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Des programmes structurés pour développer les compétences essentielles de demain
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {trainingModules?.map((module, index) => (
                <div 
                  key={index}
                  className="group bg-gray-50 rounded-2xl p-8 transition-all duration-300 hover:shadow-xl border-2 border-transparent"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = index % 2 === 0 ? '#96121D' : '#9B8E57';
                    e.currentTarget.style.transform = 'translateY(-8px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div 
                      className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                      style={{ 
                        backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)'
                      }}
                    >
                      <Icon 
                        name={module?.icon} 
                        size={32} 
                        style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {module?.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {module?.description}
                  </p>

                  <div className="space-y-3">
                    {module?.topics?.map((topic, topicIndex) => (
                      <div key={topicIndex} className="flex items-start space-x-3">
                        <div 
                          className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                          style={{ backgroundColor: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                        />
                        <span className="text-gray-700">{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3: Avantages */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Pourquoi Choisir CRF Perfection ?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Un partenaire de croissance engagé dans votre réussite
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits?.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 transition-all duration-300 hover:shadow-lg group"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div 
                    className="w-14 h-14 rounded-lg flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)'
                    }}
                  >
                    <Icon 
                      name={benefit?.icon} 
                      size={28} 
                      style={{ color: index % 2 === 0 ? '#96121D' : '#9B8E57' }}
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {benefit?.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit?.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4: CTA */}
        <section className="relative py-20 overflow-hidden" style={{ backgroundColor: '#96121D' }}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px'
            }} />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Icon name="Sparkles" size={20} style={{ color: '#9B8E57' }} />
              <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                Prêt à Transformer Vos Compétences ?
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Bâtissons Ensemble les Compétences de Demain
            </h2>
            
            <p className="text-xl text-gray-200 mb-10 leading-relaxed">
              Contactez-nous pour découvrir comment nos programmes de formation peuvent transformer 
              durablement votre organisation et propulser vos équipes vers l'excellence.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold bg-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                style={{ color: '#96121D' }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 24px rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 14px rgba(255, 255, 255, 0.2)';
                }}
              >
                Demander un Devis
                <Icon name="ArrowRight" size={20} className="ml-2" />
              </Link>
              
              <Link 
                to="/service-request-form"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg border-2 border-white transition-all duration-300"
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Télécharger le Catalogue
                <Icon name="Download" size={20} className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default TrainingDevelopmentHub;